import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, UncontrolledTooltip } from 'reactstrap';
import { ArrowLeft, Edit2, Mail, Trash2 } from 'react-feather';
import SendInvitationModal from '../../Components/BrandRequests/SendInvitationModal';
import ConfirmationModal from '../../Components/ConfirmationModal';
import { connect } from 'react-redux';
import { deleteBrandRequest as deleteBrandRequestAction } from '../../ReduxToolkit/Reducers/brandRequests';

const BrandRequestDetails = ({ deleteBrandRequest }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const brandRequest = location.state?.brandRequest;
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const inviteAlreadySent = brandRequest?.data?.invite_sent === true;
  const mailIconId = 'mail-icon-details';

  const toggleInvitationModal = () => {
    setInvitationModalOpen(!invitationModalOpen);
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const deleteId = brandRequest?.id ?? Number(id);
      if (!deleteId) return;

      const result = await deleteBrandRequest(deleteId);
      if (result?.statusCode === 200) {
        navigate('/brand-requests');
      }
    } finally {
      setDeleteLoading(false);
      setConfirmationModalOpen(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).replace(/ /g, "-");
  };

  const handleEditSection = (sectionKey) => {
    navigate('/brand-requests/create', {
      state: {
        brandRequest: {
          id: brandRequest?.id,
          ...brandRequest?.data,
        },
        editStep: sectionKey,
      },
    });
  };

  if (!brandRequest) {
    return (
      <div className="page-body" style={{ backgroundColor: '#f5f5f0', minHeight: '100vh' }}>
        <Container fluid style={{ padding: '28px 32px' }}>
          <p>Loading...</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-body" style={{ backgroundColor: '#f5f5f0', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '850px', padding: '28px 32px' }}>

        {/* Page Header */}
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <button
              type="button"
              onClick={() => navigate('/brand-requests')}
              style={backLinkStyle}
            >
              <ArrowLeft size={16} />
              <span>Brand Requests Details</span>
            </button>
            <p style={{ color: '#888', fontSize: '13px', marginTop: '4px', marginBottom: 0 }}>
              Below are the brand request details
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={() => handleEditSection('brand_info')}
              style={actionIconBtnStyle}
              title="Edit"
            >
              <Edit2 size={18} />
            </button>
            <div style={{ position: 'relative' }}>
              <button
                id={mailIconId}
                type="button"
                onClick={() => !inviteAlreadySent && toggleInvitationModal()}
                style={{
                  ...actionIconBtnStyle,
                  opacity: inviteAlreadySent ? 0.5 : 1,
                  cursor: inviteAlreadySent ? 'not-allowed' : 'pointer',
                }}
                title="Message"
                disabled={inviteAlreadySent}
              >
                <Mail size={18} />
              </button>
              {inviteAlreadySent && (
                <UncontrolledTooltip placement="top" target={mailIconId}>
                  Invite already sent
                </UncontrolledTooltip>
              )}
            </div>
            <button
              type="button"
              onClick={() => setConfirmationModalOpen(true)}
              style={actionIconBtnStyle}
              title="Delete"
            >
              <Trash2 size={18} color="#dc3545" />
            </button>
          </div>
        </div>

        {/* Brand Header Card */}
        <div style={brandHeaderStyle}>
          <div>
            <h3 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '8px', color: '#222' }}>
              {brandRequest?.data?.brand_info?.brand_name || ''}
            </h3>
            {brandRequest?.data?.brand_info?.website && (
              <a
                href={brandRequest?.data.brand_info.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007bff', fontSize: '14px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span style={{ color: '#007bff' }}>🌐</span>
                {brandRequest?.data.brand_info.website}
              </a>
            )}
            {brandRequest?.data?.brand_info?.markets_of_interest && brandRequest?.data.brand_info.markets_of_interest.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '13px', color: '#666', marginRight: '8px' }}>Markets of Interest:</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                  {brandRequest?.data.brand_info.markets_of_interest.map((market, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: '#e8f4fd',
                        color: '#0066cc',
                        border: '1px solid #b3d9f2',
                      }}
                    >
                      {typeof market === 'string' ? market : market.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          {brandRequest?.data?.brand_info?.deadline && (
            <div style={{ fontSize: '13px', color: '#666' }}>
              <span style={{ marginRight: '6px' }}>📅</span>
              Deadline: {formatDate(brandRequest?.data.brand_info.deadline)}
            </div>
          )}
        </div>

        {/* Brand In Your Space Setup Section */}
        <div style={sectionCardStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <h6 style={sectionTitleStyle}>Brand In Your Space Setup</h6>
              <p style={sectionSubtitleStyle}>Brands that are similar to your brands</p>
            </div>
            <button
              type="button"
              onClick={() => handleEditSection('brand_in_space')}
              style={editIconStyle}
            >
              <Edit2 size={16} />
            </button>
          </div>

          <div style={tableWrapperStyle}>
            <div style={tableHeaderRowStyle}>
              <div style={{ ...tableCellStyle, fontWeight: '600' }}>Brand Name</div>
              <div style={{ ...tableCellStyle, fontWeight: '600' }}>Website</div>
            </div>
            {brandRequest?.data?.brand_in_space?.brands?.map((brand, idx) => (
              <div key={idx} style={tableRowStyle}>
                <div style={tableCellStyle}>{brand?.brand_name || brand?.name || ''}</div>
                <div style={tableCellStyle}>
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'none' }}
                  >
                    {brand.website || 'https://ecostylefashion.com'}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Exclusions Section */}
        <div style={sectionCardStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <h6 style={sectionTitleStyle}>Exclusions</h6>
              <p style={sectionSubtitleStyle}>Brands / retailers you don't want to include</p>
            </div>
            <button
              type="button"
              onClick={() => handleEditSection('exclusions')}
              style={editIconStyle}
            >
              <Edit2 size={16} />
            </button>
          </div>

          <div style={tableWrapperStyle}>
            <div style={tableHeaderRowStyle}>
              <div style={{ ...tableCellStyle, fontWeight: '600' }}>Brand Name</div>
              <div style={{ ...tableCellStyle, fontWeight: '600' }}>Website</div>
            </div>
            {brandRequest?.data?.exclusions?.brands?.map((brand, idx) => (
              <div key={idx} style={tableRowStyle}>
                <div style={tableCellStyle}>{brand.name || '-'}</div>
                <div style={tableCellStyle}>
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'none' }}
                  >
                    {brand.website || '-'}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preferred Retailer Section */}
        <div style={sectionCardStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <h6 style={sectionTitleStyle}>Preferred Retailer</h6>
              <p style={sectionSubtitleStyle}>Define your preferred retailer for your brand</p>
            </div>
            <button
              type="button"
              onClick={() => handleEditSection('preferred_retailer')}
              style={editIconStyle}
            >
              <Edit2 size={16} />
            </button>
          </div>

          <div style={{ padding: '16px 20px', background: '#fafafa', borderRadius: '6px' }}>
            <p style={{ fontSize: '13px', color: '#333', margin: 0, lineHeight: '1.6' }}>
              {brandRequest?.data?.preferred_retailer || 'Premium retailer in Europe aligned with sustainable fashion'}
            </p>
          </div>
        </div>

        {/* Matching Criteria Section */}
        <div style={sectionCardStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <h6 style={sectionTitleStyle}>Matching Criteria</h6>
              <p style={sectionSubtitleStyle}>Your selected matching criteria</p>
            </div>
            <button
              type="button"
              onClick={() => handleEditSection('find_matches')}
              style={editIconStyle}
            >
              <Edit2 size={16} />
            </button>
          </div>

          <div style={{ padding: '16px 20px' }}>
            {/* Method Type */}
            {brandRequest?.data?.find_matches?.type === 'standard' && (
              <p style={criteriaTextStyle}>Standard Method</p>
            )}

            {brandRequest?.data?.find_matches?.type === 'unique' && brandRequest?.data?.find_matches?.unique_option === 'put_brand_retailers' && (
              <>
                <p style={criteriaTextStyle}>
                  <strong>Unique Method ( Put Brand B Retailers in Brand A )</strong>
                </p>

                <div style={{ marginTop: '16px' }}>
                  {(Array.isArray(brandRequest?.data?.find_matches?.brand_a)
                    ? brandRequest.data.find_matches.brand_a
                    : brandRequest?.data?.find_matches?.brand_a
                      ? [brandRequest.data.find_matches.brand_a]
                      : []
                  ).map((brand, idx) => (
                    <p key={`target-${idx}`} style={criteriaLineStyle}>
                      Target: <strong>{brand.name}</strong> ({brand.website})
                    </p>
                  ))}
                  {brandRequest?.data?.find_matches?.brand_b?.map((brand, idx) => (
                    <p key={`source-${idx}`} style={criteriaLineStyle}>
                      Source: <strong>{brand.name}</strong> ({brand.website})
                    </p>
                  ))}
                  <p style={criteriaLineStyle}>
                    Type: {brandRequest?.data?.find_matches?.match_type || 'Copy'}
                  </p>
                </div>
              </>
            )}

            {brandRequest?.data?.find_matches?.type === 'unique' && brandRequest?.data?.find_matches?.unique_option === 'take_working_retailers' && (
              <>
                <p style={criteriaTextStyle}>
                  <strong>Unique Method ( Provide Brand in Your Space )</strong>
                </p>

                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: '#666' }}>
                    Brand In Your Space Setup
                  </h6>
                  <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
                    Brands that are similar to your brands
                  </p>

                  <div style={tableWrapperStyle}>
                    <div style={tableHeaderRowStyle}>
                      <div style={{ ...tableCellStyle, fontWeight: '600', flex: 1 }}>Brand Name</div>
                      <div style={{ ...tableCellStyle, fontWeight: '600', flex: 1 }}>Website</div>
                    </div>
                    {brandRequest?.data?.find_matches?.brands_in_space?.map((brand, idx) => (
                      <div key={idx} style={tableRowStyle}>
                        <div style={{ ...tableCellStyle, flex: 1 }}>{brand.brand_name || ''}</div>
                        <div style={{ ...tableCellStyle, flex: 1 }}>
                          <a
                            href={brand.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', textDecoration: 'none' }}
                          >
                            {brand.website || 'https://ecostylefashion.com'}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p style={criteriaLineStyle}>
                  Type: {brandRequest?.data?.find_matches?.match_type || 'Copy'}
                </p>
              </>
            )}
          </div>
        </div>

      </Container>

      {/* Send Invitation Modal */}
      <SendInvitationModal
        isOpen={invitationModalOpen}
        toggle={toggleInvitationModal}
        brandRequest={brandRequest}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        isLoading={deleteLoading}
        onSubmit={handleDelete}
        title="Delete Brand Request"
        message="Are you sure you want to permanently delete this brand request?"
      />
    </div>
  );
};

const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: '18px',
  fontWeight: '600',
  color: '#222',
  cursor: 'pointer',
};

const actionIconBtnStyle = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: '6px',
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#333',
  transition: 'all 0.2s',
};

const brandHeaderStyle = {
  background: '#fff',
  borderRadius: '10px',
  padding: '24px 28px',
  marginBottom: '20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const sectionCardStyle = {
  background: '#fff',
  borderRadius: '10px',
  padding: '24px 28px',
  marginBottom: '20px',
};

const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '20px',
};

const sectionTitleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#222',
  marginBottom: '4px',
};

const sectionSubtitleStyle = {
  fontSize: '12px',
  color: '#888',
  margin: 0,
};

const editIconStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  color: '#666',
};

const tableWrapperStyle = {
  border: '1px solid #e5e5e5',
  borderRadius: '6px',
  overflow: 'hidden',
};

const tableHeaderRowStyle = {
  display: 'flex',
  background: '#f8f8f8',
  padding: '12px 16px',
  borderBottom: '1px solid #e5e5e5',
};

const tableRowStyle = {
  display: 'flex',
  padding: '12px 16px',
  borderBottom: '1px solid #f0f0f0',
};

const tableCellStyle = {
  flex: 1,
  fontSize: '13px',
  color: '#333',
};

const criteriaTextStyle = {
  fontSize: '14px',
  color: '#333',
  margin: 0,
  lineHeight: '1.6',
};

const criteriaLineStyle = {
  fontSize: '13px',
  color: '#666',
  margin: '4px 0',
  lineHeight: '1.6',
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {
  deleteBrandRequest: deleteBrandRequestAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(BrandRequestDetails);